import { defineComponent as _defineComponent } from 'vue';
import { useElementBounding, useScroll } from "@vueuse/core";
import { storeToRefs } from "pinia";
import { computed, provide, reactive, ref, watch, watchEffect } from "vue";
import { DatatypesMapperModel } from "@/components/Datatypes/model";
import { useWorkflowStores } from "@/composables/workflowStores";
import { assertDefined } from "@/utils/assertions";
import { useD3Zoom } from "./composables/d3Zoom";
import { useViewportBoundingBox } from "./composables/viewportBoundingBox";
import { maxZoom, minZoom } from "./modules/zoomLevels";
import AdaptiveGrid from "./AdaptiveGrid.vue";
import WorkflowComment from "./Comments/WorkflowComment.vue";
import BoxSelectPreview from "./Tools/BoxSelectPreview.vue";
import InputCatcher from "./Tools/InputCatcher.vue";
import ToolBar from "./Tools/ToolBar.vue";
import WorkflowNode from "@/components/Workflow/Editor/Node.vue";
import WorkflowEdges from "@/components/Workflow/Editor/WorkflowEdges.vue";
import WorkflowMinimap from "@/components/Workflow/Editor/WorkflowMinimap.vue";
import ZoomControl from "@/components/Workflow/Editor/ZoomControl.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'WorkflowGraph',
    props: {
        steps: { type: Object, required: true },
        datatypesMapper: { type: DatatypesMapperModel, required: true },
        highlightId: { type: Number, default: null },
        scrollToId: { type: Number, default: null },
        readonly: { type: Boolean, default: false },
        initialPosition: { type: Object, default: () => ({ x: 50, y: 20 }) },
        isInvocation: { type: Boolean, default: false },
        showMinimap: { type: Boolean, default: true },
        showZoomControls: { type: Boolean, default: true },
    },
    emits: ["transform", "graph-offset", "onRemove", "scrollTo"],
    setup(__props, { expose, emit }) {
        const props = __props;
        const { stateStore, stepStore } = useWorkflowStores();
        const { scale, activeNodeId, draggingPosition, draggingTerminal } = storeToRefs(stateStore);
        const canvas = ref(null);
        const elementBounding = useElementBounding(canvas, { windowResize: false, windowScroll: false });
        const scroll = useScroll(canvas);
        const { transform, panBy, setZoom, moveTo } = useD3Zoom(scale.value, minZoom, maxZoom, canvas, scroll, props.initialPosition);
        expose({
            setZoom,
            moveTo,
        });
        const { viewportBoundingBox } = useViewportBoundingBox(elementBounding, scale, transform);
        const isDragging = ref(false);
        provide("isDragging", isDragging);
        provide("transform", transform);
        watch(() => props.scrollToId, () => {
            if (props.scrollToId !== null) {
                const scrollToPosition = stateStore.stepPosition[props.scrollToId];
                const step = stepStore.getStep(props.scrollToId);
                assertDefined(scrollToPosition);
                assertDefined(step);
                const { width: stepWidth, height: stepHeight } = scrollToPosition;
                const { position: stepPosition } = step;
                if (stepPosition) {
                    const { width, height } = reactive(elementBounding);
                    const centerScreenX = width / 2;
                    const centerScreenY = height / 2;
                    const offsetX = centerScreenX - (stepPosition.left + stepWidth / 2);
                    const offsetY = centerScreenY - (stepPosition.top + stepHeight / 2);
                    onZoom(1, { x: offsetX, y: offsetY });
                }
                else {
                    console.log("Step has no position");
                }
                emit("scrollTo");
            }
        });
        function onZoom(zoomLevel, panTo = null) {
            setZoom(zoomLevel);
            if (panTo) {
                panBy({ x: panTo.x - transform.value.x, y: panTo.y - transform.value.y });
            }
            stateStore.scale = zoomLevel;
        }
        function onStopDragging() {
            stateStore.draggingPosition = null;
            stateStore.draggingTerminal = null;
            isDragging.value = false;
        }
        function onDragConnector(position, draggingTerminal) {
            stateStore.draggingPosition = position;
            stateStore.draggingTerminal = draggingTerminal;
            isDragging.value = true;
        }
        function onActivate(nodeId) {
            if (activeNodeId.value !== nodeId) {
                stateStore.activeNodeId = nodeId;
            }
        }
        function onDeactivate() {
            stateStore.activeNodeId = null;
        }
        watch(() => transform.value.k, () => (stateStore.scale = transform.value.k));
        watch(transform, () => emit("transform", transform.value));
        watchEffect(() => {
            emit("graph-offset", reactive(elementBounding));
        });
        const canvasStyle = computed(() => {
            return { transform: `translate(${transform.value.x}px, ${transform.value.y}px) scale(${transform.value.k})` };
        });
        const { commentStore } = useWorkflowStores();
        const { comments } = storeToRefs(commentStore);
        return { __sfc: true, emit, props, stateStore, stepStore, scale, activeNodeId, draggingPosition, draggingTerminal, canvas, elementBounding, scroll, transform, panBy, setZoom, moveTo, viewportBoundingBox, isDragging, onZoom, onStopDragging, onDragConnector, onActivate, onDeactivate, canvasStyle, commentStore, comments, AdaptiveGrid, WorkflowComment, BoxSelectPreview, InputCatcher, ToolBar, WorkflowNode, WorkflowEdges, WorkflowMinimap, ZoomControl };
    }
});
